import { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { Sparkles, CalendarClock, Loader2 } from 'lucide-react';
import {
  CATEGORIES,
  CATEGORY_META,
  RECURRING_TYPES,
  RECURRING_TYPE_META,
  type Expense,
  type ExpenseFormData,
  type RecurringType,
} from '../../types';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { calcNextDue } from '../../lib/recurring';
import { useCategories } from '../../hooks/useCategories';
import { isPremiumPlan } from '../../lib/plans';
import toast from 'react-hot-toast';

const schema = z.object({
  description: z.string().min(1, 'Description is required').max(255),
  amount: z
    .number({ invalid_type_error: 'Enter a valid amount' })
    .positive('Amount must be positive')
    .multipleOf(0.01, 'Max 2 decimal places'),
  category: z.string().min(1, 'Seleziona o scrivi una categoria'),
  date: z.string().min(1, 'Date is required'),
  recurring_type: z.enum(RECURRING_TYPES as [RecurringType, ...RecurringType[]]),
  frequency_days: z.number().int().positive().nullable().optional(),
});

type FormData = z.infer<typeof schema>;

interface ExpenseFormProps {
  expense?: Expense;
  onSuccess: () => void;
  onCancel: () => void;
  onCreate?: (data: ExpenseFormData) => Promise<Expense>;
  onUpdate?: (id: string, data: Partial<ExpenseFormData>) => Promise<void>;
}

const durationOptions = RECURRING_TYPES.filter(r => r !== 'none').map(r => ({
  value: r,
  label: `${RECURRING_TYPE_META[r].icon} ${RECURRING_TYPE_META[r].label}`,
}));

export default function ExpenseForm({
  expense,
  onSuccess,
  onCancel,
  onCreate,
  onUpdate,
}: ExpenseFormProps) {
  const { profile, session } = useAuth();
  const { categories: customCats } = useCategories();
  const [aiLoading, setAiLoading] = useState(false);

  const isPremium = isPremiumPlan(profile?.plan);
  const isEdit = Boolean(expense);

  // Dynamic category options merging defaults and DB
  const categoryOptions = useMemo(() => {
    // defaults
    const defaults = CATEGORIES.map(c => ({
      value: c,
      label: `${CATEGORY_META[c].icon} ${CATEGORY_META[c].label}`,
    }));
    // customs
    const customs = customCats.map(c => ({
      value: c.name,
      label: `${c.emoji} ${c.name}`,
    }));
    
    // Merge, don't duplicate (in case custom overrides)
    const map = new Map<string, { value: string, label: string }>();
    defaults.forEach(d => map.set(d.value.toLowerCase(), d));
    customs.forEach(c => map.set(c.value.toLowerCase(), c));
    return Array.from(map.values());
  }, [customCats]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      description:    expense?.description    ?? '',
      amount:         expense?.amount         ?? ('' as unknown as number),
      category:       expense?.category       ?? 'other',
      date:           expense?.date           ?? format(new Date(), 'yyyy-MM-dd'),
      recurring_type: expense?.recurring_type ?? 'none',
      frequency_days: expense?.frequency_days ?? null,
    },
  });

  const description   = watch('description');
  const recurringType = watch('recurring_type');
  const dateValue     = watch('date');
  const freqDays      = watch('frequency_days');
  const isRecurring   = recurringType !== 'none';

  // Live next_due preview
  const nextDuePreview = isRecurring
    ? calcNextDue(dateValue || format(new Date(), 'yyyy-MM-dd'), recurringType, freqDays)
    : null;

  // AI auto-categorize (premium only)
  const aiCategorize = async () => {
    if (!description || !isPremium) return;
    setAiLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('ai-categorize', {
        body: { description },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (error) throw error;
      
      const { category, emoji } = data || {};
      
      if (category) {
        // Find existing option to match case exactly, otherwise use raw
        const existing = categoryOptions.find(opt => opt.value.toLowerCase() === category.toLowerCase());
        const catValue = existing ? existing.value : category;
        
        setValue('category', catValue, { shouldValidate: true });
        
        toast.success(
          <div className="flex flex-col gap-1">
            <span className="font-semibold">AI Magic ✨</span>
            <span className="text-sm">Assigned: {emoji} {catValue}</span>
          </div>
        );
      }
    } catch {
      toast.error('AI categorization failed');
      setValue('category', 'other', { shouldValidate: true });
    } finally {
      setAiLoading(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      const payload: ExpenseFormData = {
        description:    data.description,
        amount:         data.amount,
        category:       data.category as ExpenseFormData['category'],
        recurring:      data.recurring_type !== 'none',
        recurring_type: data.recurring_type,
        frequency_days: data.frequency_days ?? null,
        next_due:       calcNextDue(data.date, data.recurring_type, data.frequency_days),
        date:           data.date,
      };

      if (isEdit && expense && onUpdate) {
        await onUpdate(expense.id, payload);
        toast.success('Expense updated');
      } else if (onCreate) {
        await onCreate(payload);
        toast.success(isRecurring ? 'Ricorrenza salvata ✓' : 'Expense added');
      }
      onSuccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save expense');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      {/* Description + AI button */}
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <Input
            label="Description"
            placeholder="e.g. Netflix, Affitto, Palestra"
            error={errors.description?.message}
            {...register('description')}
          />
        </div>
        {isPremium && (
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={aiCategorize}
            disabled={aiLoading}
            title="Auto-categorize with AI"
            className="mb-[1px] border border-brand-500/40 hover:border-brand-500/70"
          >
            {aiLoading ? (
              <Loader2 size={14} className="text-brand-400 animate-spin" />
            ) : (
              <Sparkles size={14} className="text-brand-400" />
            )}
            <span className="ml-1 text-s text-brand-300 font-medium">AI Magic</span>
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          type="number"
          label="Amount (€)"
          placeholder="0.00"
          step="0.01"
          min="0.01"
          error={errors.amount?.message}
          {...register('amount', { valueAsNumber: true })}
        />
        <Input
          type="date"
          label={isRecurring ? 'Prima scadenza' : 'Date'}
          error={errors.date?.message}
          {...register('date')}
        />
      </div>

      <Controller
        name="category"
        control={control}
        render={({ field }) => {
          // If the AI returned a new category not yet in the options (before refetch completes), let's render it safely.
          const currentIsMissing = field.value && !categoryOptions.some(o => o.value === field.value);
          const activeOptions = currentIsMissing
            ? [...categoryOptions, { value: field.value, label: `✨ ${field.value}` }]
            : categoryOptions;

          return (
            <Select
              label="Category"
              options={activeOptions}
              error={errors.category?.message}
              {...field}
            />
          );
        }}
      />

      {/* ── Recurring section ─────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-300">Spesa ricorrente</span>
        <button
          type="button"
          role="switch"
          aria-checked={isRecurring}
          onClick={() =>
            setValue(
              'recurring_type',
              isRecurring ? 'none' : 'monthly',
              { shouldValidate: true },
            )
          }
          className={[
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-slate-900',
            isRecurring ? 'bg-brand-600' : 'bg-slate-700',
          ].join(' ')}
        >
          <span
            className={[
              'inline-block h-4 w-4 rounded-full bg-white shadow transition-transform',
              isRecurring ? 'translate-x-6' : 'translate-x-1',
            ].join(' ')}
          />
        </button>
      </div>

      {isRecurring && (
        <Controller
          name="recurring_type"
          control={control}
          render={({ field }) => (
            <Select
              label="Frequenza"
              options={durationOptions}
              error={errors.recurring_type?.message}
              {...field}
            />
          )}
        />
      )}

      {isRecurring && (
        <div className="rounded-xl border border-brand-500/25 bg-brand-500/5 px-4 py-3 flex items-start gap-3">
          <CalendarClock size={16} className="text-brand-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-brand-300">Prossima scadenza automatica</p>
            <p className="text-sm text-slate-200 font-semibold mt-0.5">
              {nextDuePreview ? nextDuePreview : '—'}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Apparirà nella sezione "Prossimi ricorrenti" della dashboard.
            </p>
          </div>
        </div>
      )}

      {!isPremium && (
        <p className="text-xs text-slate-500 flex items-center gap-1">
          <Sparkles size={12} className="text-brand-500" />
          <span>
            Upgrade a <span className="text-brand-400 font-medium">Premium</span> per
            AI auto-categorizzazione.
          </span>
        </p>
      )}

      <div className="flex gap-3 pt-2">
        <Button type="button" variant="secondary" fullWidth onClick={onCancel}>
          Annulla
        </Button>
        <Button type="submit" fullWidth loading={isSubmitting}>
          {isEdit ? 'Salva modifiche' : isRecurring ? 'Salva ricorrenza' : 'Aggiungi spesa'}
        </Button>
      </div>
    </form>
  );
}


